// Nút «Tiếp tục với Google» — GSI auth-code popup, GỘP đăng nhập + Google Calendar trong 1 consent
// (giống Core connect). Dùng chung cho trang đăng nhập và trang nhận lời mời: nút chỉ lo xin `code`,
// đổi code lấy phiên thì trang truyền vào qua `onCode` — mỗi trang một endpoint BE
// (/auth/google · /public/invitations/:token/accept-google), cùng hàm xác minh ở googleService.
//
// initCodeClient(popup) trả `code` → BE exchange (client secret từ file JSON, redirect_uri='postmessage')
// → lưu refresh token calendar vào metadata.google_calendar. KHÔNG còn bước «Kết nối Calendar» lần 2.

import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import toast from 'react-hot-toast';

// Nút Google chỉ hiện khi có client id — build thiếu `.env.production` sẽ tự ẩn (trang gọi tự kiểm).
export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_WEB_CLIENT_ID || '';

const GOOGLE_SCOPE = 'openid email profile https://www.googleapis.com/auth/calendar.events';

export default function GoogleCodeButton({ onCode }) {
  const { t } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  const codeClientRef = useRef(null);
  // `onCode` và câu dịch đổi theo từng lần render — giữ bản mới nhất trong ref để client GSI chỉ khởi
  // tạo MỘT lần. Cho chúng vào deps của effect là dựng lại client mỗi render (bẫy «t vào deps = loop»).
  const noCodeMsg = t`Không nhận được mã Google`;
  const latest = useRef({ onCode, noCodeMsg });
  useEffect(() => { latest.current = { onCode, noCodeMsg }; });

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;
    let cancelled = false;
    let tries = 0;
    const init = () => {
      if (cancelled) return;
      const oauth2 = window.google?.accounts?.oauth2;
      if (!oauth2?.initCodeClient) {
        if (tries++ < 40) setTimeout(init, 100);
        return;
      }
      try {
        codeClientRef.current = oauth2.initCodeClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: GOOGLE_SCOPE,
          ux_mode: 'popup',
          callback: async (resp) => {
            if (!resp?.code) {
              toast.error(latest.current.noCodeMsg);
              setSubmitting(false);
              return;
            }
            try {
              await latest.current.onCode(resp.code);
            } finally {
              setSubmitting(false);
            }
          },
          error_callback: () => setSubmitting(false),
        });
      } catch (e) {
        console.warn('GSI code client init failed:', e?.message);
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const startGoogle = () => {
    if (!codeClientRef.current) { toast.error(t`Google chưa sẵn sàng, thử lại`); return; }
    setSubmitting(true);
    codeClientRef.current.requestCode();
  };

  return (
    <button
      type="button"
      onClick={startGoogle}
      disabled={submitting}
      className="w-full flex items-center justify-center gap-3 py-2.5 px-4 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-60 transition"
    >
      {submitting ? (
        <>
          <Loader2 size={16} className="animate-spin text-slate-500" />
          <span className="text-sm font-medium text-slate-700">{t`Đang đăng nhập với Google...`}</span>
        </>
      ) : (
        <>
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.13 4.13 0 0 1-1.8 2.71v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A8.99 8.99 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.96 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3-2.33z" />
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A8.99 8.99 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z" />
          </svg>
          <span className="text-sm font-medium text-slate-700">{t`Tiếp tục với Google`}</span>
        </>
      )}
    </button>
  );
}
